import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, 
  Zap, 
  Search, 
  FileText, 
  Target, 
  BarChart3, 
  Settings, 
  Menu, 
  X,
  Bell,
  Beaker,
  Sparkles,
  Rocket,
  ChevronDown,
  ChevronRight,
  Wrench,
  Copy,
  ShoppingCart,
  Activity,
  Building2,
  CreditCard,
  Lightbulb,
  TrendingUp,
  Award,
  HelpCircle,
  Shield,
  PieChart,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  CheckCheck,
  DollarSign,
  BadgePercent,
  ListChecks,
  ShieldCheck,
  History,
  Bot
} from 'lucide-react';

// Context imports
import { AccountProvider, useAccount } from './context/AccountContext';

// Component imports
import AuthGuard from './components/AuthGuard';
import UserMenu from './components/UserMenu';
import Dashboard from './components/dashboard';
import OptimizationEngine from './components/OptimizationEngine';
import KeywordResearch from './components/KeywordResearch';
import AdCreator from './components/AdCreator';
import CampaignBuilder from './components/CampaignBuilder';
import ABTesting from './components/ABTesting';
import AdminDashboard from './components/AdminDashboard';
import AICampaignGenerator from './components/AICampaignGenerator';
import CampaignCloner from './components/CampaignCloner';
import ProductFeedManager from './components/ProductFeedManager';
import ServerSideTracking from './components/ServerSideTracking';
import OfferAnalyzer from './components/OfferAnalyzer';
import QualityScoreOptimizer from './components/QualityScoreOptimizer';
import PmaxOptimizer from './components/PmaxOptimizer';
import SearchTermAnalyzer from './components/SearchTermAnalyzer';
import BusinessProfile from './components/BusinessProfile';
import BudgetAllocator from './components/BudgetAllocator';
import Reports from './components/Reports';
import PartnerDashboard from './components/PartnerDashboard';
import AffiliateDashboard from './components/AffiliateDashboard';
import AdminAffiliates from './components/AdminAffiliates';
import AffiliateSignup from './components/AffiliateSignup';
import SubscriptionPlans from './components/SubscriptionPlans';
import AccountSelector from './components/AccountSelector';
import CheckoutSuccess from './components/CheckoutSuccess';
import CheckoutCancel from './components/CheckoutCancel';
import SettingsPage from './components/Settings';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import LandingPage from './components/LandingPage';
import AcceptInvite from './components/AcceptInvite';
import CompetitionDashboard from './components/CompetitionDashboard';
import OnboardingChecklist from './components/OnboardingChecklist';
import AccountAudit from './components/AccountAudit';
import AuditLog from './components/AuditLog';
import AlertRules from './components/AlertRules';
import AccountChat from './components/AccountChat';
import { HelpPage } from './components/Help';
import AdSpendBlocker, { useAdSpendBlocker } from './components/AdSpendBlocker';
import ThemeWrapper from './components/ThemeWrapper';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import TrialExpiredModal from './components/TrialExpiredModal';
import { useAuth } from '@clerk/clerk-react';
import { setAuthTokenGetter, setTrialExpiredCallback } from './api';
import { setAuthTokenGetter as setAuthTokenGetterUtils, setTrialExpiredCallback as setTrialExpiredCallbackUtils } from './utils/api';
import { useTheme } from './context/ThemeContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api`;

function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const then = new Date(timestamp).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

function AppContent({ selectedAccount, clearAccount }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectAccount } = useAccount();
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sharedCampaignData, setSharedCampaignData] = useState(null);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [aiGeneratedCampaign, setAiGeneratedCampaign] = useState(null);
  const [campaignToEdit, setCampaignToEdit] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({
    'campaign-creation': false, // Closed by default
    'advanced': false,
    'analytics': false
  });
  const [allAccounts, setAllAccounts] = useState([]);
  const [mccHierarchy, setMccHierarchy] = useState(null);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [dismissedKeys, setDismissedKeys] = useState(() => new Set());
  const [hoveredNotificationKey, setHoveredNotificationKey] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [affiliateStatus, setAffiliateStatus] = useState(null); // null | 'pending' | 'approved' | 'rejected' | 'paused'
  // Onboarding checklist meta — drives the "Get started" sidebar entry and its
  // progress badge. We refetch when the user navigates so the badge updates.
  const [onboardingMeta, setOnboardingMeta] = useState(null);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/admin/check`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success && data.isAdmin) setIsAdmin(true);
      } catch (e) {}
    };
    checkAdmin();
  }, [getToken]);

  useEffect(() => {
    const checkPartner = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/partner/check`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success && data.isPartner) setIsPartner(true);
      } catch (e) {}
    };
    checkPartner();
  }, [getToken]);

  // Lightweight check for affiliate status — drives the sidebar item and
  // the pure-affiliate routing redirect below.
  useEffect(() => {
    const checkAffiliate = async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const response = await fetch(`${API_URL}/affiliate/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data?.success && data.affiliate?.status) {
          setAffiliateStatus(data.affiliate.status);
        }
      } catch (e) { /* silent — affiliate is optional */ }
    };
    checkAffiliate();
  }, [getToken]);

  // Onboarding status — refetched whenever the active view changes. Cheap
  // (one auth'd query) and keeps the sidebar progress badge in sync as soon
  // as the user finishes a milestone in another tool.
  useEffect(() => {
    const fetchOnboarding = async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const response = await fetch(`${API_URL}/onboarding/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data?.success) {
          setOnboardingMeta({
            completedCount: data.completedCount,
            total: data.total,
            progressPct: data.progressPct,
            hideTab: data.hideTab,
            stickyCardVisible: data.stickyCardVisible
          });
        }
      } catch (e) { /* silent — onboarding is non-critical UX */ }
    };
    fetchOnboarding();
  }, [getToken, activeView]);

  // Pure-affiliate auto-redirect: when an approved affiliate has no Google
  // Ads connections, the regular dashboard is empty and confusing. Land them
  // directly on /affiliate-dashboard. Gated by sessionStorage so the user
  // can still navigate away and stay there.
  useEffect(() => {
    if (sessionStorage.getItem('affiliate_default_routed') === '1') return;
    if (affiliateStatus !== 'approved') return;
    if ((allAccounts || []).length > 0) return;
    if (activeView !== 'dashboard') return;
    setActiveView('affiliate-dashboard');
    sessionStorage.setItem('affiliate_default_routed', '1');
  }, [affiliateStatus, allAccounts, activeView]);

  // Affiliate referral attribution: on first authenticated load this session,
  // read the bidoryx_ref cookie set on bidoryx.com and POST it to the backend.
  // The backend ignores duplicates so calling this every session is safe — we
  // still gate on sessionStorage to skip the network round-trip after the first.
  useEffect(() => {
    const claimReferral = async () => {
      try {
        if (sessionStorage.getItem('bidoryx_ref_claimed') === '1') return;

        const cookieMatch = document.cookie.match(/(?:^|;\s*)bidoryx_ref=([^;]+)/);
        const refCode = cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;
        if (!refCode) {
          sessionStorage.setItem('bidoryx_ref_claimed', '1');
          return;
        }

        const token = await getToken();
        if (!token) return; // not authenticated yet, retry on next render

        await fetch(`${API_URL}/affiliate/claim-referral`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ refCode, source: 'cookie' })
        });
        sessionStorage.setItem('bidoryx_ref_claimed', '1');
      } catch (e) {
        // Attribution should never break the app — log and move on.
        console.warn('Affiliate referral claim failed:', e?.message || e);
      }
    };
    claimReferral();
  }, [getToken]);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set up auth token getter for BOTH API utilities
  useEffect(() => {
    setAuthTokenGetter(getToken); // For api.js
    setAuthTokenGetterUtils(getToken); // For utils/api.js
  }, [getToken]);

  // Set up trial expired callback for BOTH API utilities
  useEffect(() => {
    const handleTrialExpired = () => {
      setShowTrialExpiredModal(true);
    };
    setTrialExpiredCallback(handleTrialExpired);
    setTrialExpiredCallbackUtils(handleTrialExpired);
  }, []);

  // Sync activeView with URL
  useEffect(() => {
    const path = location.pathname.substring(1) || 'dashboard';
    setActiveView(path);
    // Force dashboard remount when navigating to it (via any method)
    if (path === 'dashboard') {
      setDashboardKey(prev => prev + 1);
    }
  }, [location]);

  // Navigate when activeView changes
  const handleNavigate = (view) => {
    setActiveView(view);
    navigate(`/${view}`);
    // Note: dashboardKey is incremented in the location useEffect
    // Close mobile sidebar after navigation
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  useEffect(() => {
    fetchAllAccounts();
  }, []);

  // Fetch notifications on mount, when account changes, and every 2 minutes
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 120000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount?.id, selectedAccount?.customer_id]);

  // Refresh feed when the user opens the bell so it always feels current
  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNotifications]);

  const fetchAllAccounts = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/user/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success && data.accounts) {
        setAllAccounts(data.accounts);
        if (data.mccAccounts && data.hierarchy) {
          setMccHierarchy({
            mccAccounts: data.mccAccounts,
            hierarchy: data.hierarchy,
            orphanAccountIds: data.orphanAccountIds || []
          });
        }
        console.log(`✅ Loaded ${data.accounts.length} accounts (${data.mccAccounts?.length || 0} MCCs) for dropdown`);
      }
    } catch (error) {
      console.error('Error fetching user accounts:', error);
    }
  };

  // Combined notifications feed: persistent (DB) + live alerts (dashboard) + ad-spend warnings
  const fetchNotifications = async () => {
    try {
      setNotificationsLoading(true);
      const token = await getToken();
      const headers = { 'Authorization': `Bearer ${token}` };

      const accountId = selectedAccount?.id || selectedAccount?.customer_id;
      const accountHeaders = accountId
        ? { ...headers, 'X-Account-Id': accountId }
        : headers;

      const [persistRes, alertsRes, spendRes] = await Promise.allSettled([
        fetch(`${API_URL}/notifications?limit=20`, { headers }),
        accountId
          ? fetch(`${API_URL}/dashboard/alerts`, { headers: accountHeaders })
          : Promise.resolve(null),
        fetch(`${API_URL}/user/ad-spend-status`, { headers })
      ]);

      const items = [];

      if (persistRes.status === 'fulfilled' && persistRes.value) {
        try {
          const data = await persistRes.value.json();
          if (data?.success && Array.isArray(data.notifications)) {
            data.notifications.forEach(n => {
              items.push({
                key: `db-${n.id}`,
                source: 'db',
                id: n.id,
                type: n.type,
                severity: n.severity || 'info',
                title: n.title,
                message: n.message,
                actionLabel: n.action_label,
                actionUrl: n.action_url,
                isRead: !!n.is_read,
                timestamp: n.created_at
              });
            });
          }
        } catch (e) {
          console.warn('Could not parse notifications response', e);
        }
      }

      if (alertsRes.status === 'fulfilled' && alertsRes.value) {
        try {
          const data = await alertsRes.value.json();
          if (data?.success && Array.isArray(data.alerts)) {
            data.alerts.slice(0, 10).forEach((a, idx) => {
              items.push({
                key: `alert-${a.id || idx}`,
                source: 'alert',
                id: null,
                type: a.type || 'performance',
                severity: a.severity || 'warning',
                title: a.campaign ? `${a.campaign}` : 'Performance alert',
                message: a.message,
                actionLabel: null,
                actionUrl: null,
                isRead: true, // live alerts are not stored, treat as 'seen' for badge calc
                timestamp: a.timestamp || new Date().toISOString()
              });
            });
          }
        } catch (e) {
          console.warn('Could not parse alerts response', e);
        }
      }

      if (spendRes.status === 'fulfilled' && spendRes.value) {
        try {
          const data = await spendRes.value.json();
          if (data?.success) {
            if (data.isOverLimit) {
              items.unshift({
                key: 'ad-spend-over',
                source: 'spend',
                id: null,
                type: 'ad_spend_over',
                severity: 'critical',
                title: 'Ad spend over tier limit',
                message: `You've spent €${(data.currentSpend || 0).toLocaleString()} of €${(data.tierLimit || 0).toLocaleString()} (${data.percentage}%). Upgrade to maintain access.`,
                actionLabel: 'Upgrade plan',
                actionUrl: '/settings',
                isRead: false,
                timestamp: new Date().toISOString()
              });
            } else if (typeof data.percentage === 'number' && data.percentage >= 80) {
              items.unshift({
                key: 'ad-spend-warn',
                source: 'spend',
                id: null,
                type: 'ad_spend_warning',
                severity: 'warning',
                title: 'Ad spend nearing your limit',
                message: `${data.percentage}% of your ${data.currentTierName || 'plan'} limit used (€${(data.currentSpend || 0).toLocaleString()} / €${(data.tierLimit || 0).toLocaleString()}).`,
                actionLabel: 'View subscription',
                actionUrl: '/settings',
                isRead: false,
                timestamp: new Date().toISOString()
              });
            }
          }
        } catch (e) {
          console.warn('Could not parse ad spend response', e);
        }
      }

      // Sort newest first; prioritise unread
      items.sort((a, b) => {
        if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });

      // Hide anything the user dismissed during this session (live alerts/spend
      // warnings have no DB row so we keep them muted client-side only).
      const visible = items.filter(n => !dismissedKeys.has(n.key));

      setNotifications(visible);
      setUnreadCount(visible.filter(n => !n.isRead).length);
    } catch (error) {
      console.warn('Error fetching notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const markNotificationRead = async (notification) => {
    if (!notification) return;
    try {
      if (notification.source === 'db' && notification.id) {
        const token = await getToken();
        await fetch(`${API_URL}/notifications/${notification.id}/read`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setNotifications(prev => prev.map(n =>
        n.key === notification.key ? { ...n, isRead: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - (notification.isRead ? 0 : 1)));
    } catch (error) {
      console.warn('Failed to mark notification read', error);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      const token = await getToken();
      await fetch(`${API_URL}/notifications/read-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.warn('Failed to mark all notifications read', error);
    }
  };

  const dismissNotification = async (notification) => {
    if (!notification) return;
    // Optimistic remove from the list + remember so re-fetch doesn't re-add it.
    setNotifications(prev => prev.filter(n => n.key !== notification.key));
    setUnreadCount(prev => Math.max(0, prev - (notification.isRead ? 0 : 1)));
    setDismissedKeys(prev => {
      const next = new Set(prev);
      next.add(notification.key);
      return next;
    });

    if (notification.source === 'db' && notification.id) {
      try {
        const token = await getToken();
        await fetch(`${API_URL}/notifications/${notification.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.warn('Failed to dismiss notification', error);
      }
    }
  };

  const dismissAllNotifications = async () => {
    if (notifications.length === 0) return;
    const keys = notifications.map(n => n.key);
    const hadDbItems = notifications.some(n => n.source === 'db');

    setNotifications([]);
    setUnreadCount(0);
    setDismissedKeys(prev => {
      const next = new Set(prev);
      keys.forEach(k => next.add(k));
      return next;
    });

    if (hadDbItems) {
      try {
        const token = await getToken();
        await fetch(`${API_URL}/notifications`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.warn('Failed to clear notifications', error);
      }
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markNotificationRead(notification);
    }
    if (notification.actionUrl && notification.actionUrl.startsWith('/')) {
      const view = notification.actionUrl.replace(/^\//, '');
      if (view) {
        handleNavigate(view);
        setShowNotifications(false);
      }
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAccountDropdown) {
        setShowAccountDropdown(false);
      }
      if (showNotifications) {
        setShowNotifications(false);
      }
    };
    
    if (showAccountDropdown || showNotifications) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showAccountDropdown, showNotifications]);

  // "Get started" badge: x/8 while in progress, hidden when onboarding is
  // fully complete or the 14-day grace window has passed. Admins always
  // see the tab so they can inspect / QA the flow at any time.
  const showOnboardingItem = !!onboardingMeta && (isAdmin || !onboardingMeta.hideTab);
  const onboardingBadge = onboardingMeta
    ? `${onboardingMeta.completedCount}/${onboardingMeta.total}`
    : null;

  const menuItems = [
    { 
      id: 'dashboard', 
      icon: LayoutDashboard, 
      label: 'Dashboard', 
      color: '#6366f1' 
    },
    ...(showOnboardingItem
      ? [{ id: 'getting-started', icon: ListChecks, label: 'Get started', color: '#8b5cf6', badge: onboardingBadge }]
      : []),
    { id: 'account-audit', icon: ShieldCheck, label: 'Account Audit', color: '#0ea5e9', badge: 'New' },
    ...(isAdmin ? [{ id: 'business-profile', icon: Building2, label: 'Business Profile', color: '#6366f1', badge: 'Admin' }] : []),
    {
      id: 'ai-optimization',
      icon: Zap,
      label: 'AI Optimization',
      color: '#f59e0b',
      isParent: true,
      children: [
        { id: 'bid-optimizer', icon: TrendingUp, label: 'Bid Optimizer', color: '#f59e0b' },
        { id: 'budget-allocator', icon: PieChart, label: 'Budget Allocator', color: '#8b5cf6' },
        { id: 'search-term-analyzer', icon: Search, label: 'Search Term Analyzer', color: '#06b6d4' },
        { id: 'quality-score', icon: Award, label: 'Quality Score Optimizer', color: '#10b981' },
        ...(isAdmin ? [{ id: 'pmax-optimizer', icon: Rocket, label: 'PMax Optimizer', color: '#8b5cf6', badge: 'Pro' }] : [])
      ]
    },
    {
      id: 'campaign-creation',
      icon: Rocket,
      label: 'Campaign Creation',
      color: '#8b5cf6',
      isParent: true,
      children: [
        ...(isAdmin ? [{ id: 'ai-generator', icon: Sparkles, label: 'AI Campaign Generator', color: '#8b5cf6', badge: 'Admin' }] : []),
        { id: 'campaign-builder', icon: Target, label: 'Campaign Builder', color: '#3b82f6' },
        { id: 'keyword-research', icon: Search, label: 'Keyword Research', color: '#10b981' },
        { id: 'ad-creator', icon: FileText, label: 'Ad Creator', color: '#f59e0b' },
        { id: 'offer-analyzer', icon: Lightbulb, label: 'Offer Analyzer', color: '#8b5cf6' }
      ]
    },
    {
      id: 'advanced',
      icon: Wrench,
      label: 'Advanced',
      color: '#64748b',
      isParent: true,
      children: [
        { id: 'alert-rules', icon: Bell, label: 'Alert Rules', color: '#f59e0b', badge: 'New' },
        { id: 'audit-log', icon: History, label: 'Audit Log', color: '#06b6d4', badge: 'New' },
        { id: 'ab-testing', icon: Beaker, label: 'A/B Testing', color: '#06b6d4' },
        { id: 'campaign-cloner', icon: Copy, label: 'Campaign Cloner', color: '#8b5cf6' },
        { id: 'product-feeds', icon: ShoppingCart, label: 'Product Feed Manager', color: '#ec4899', badge: 'Beta' }
      ]
    },
    {
      id: 'competition',
      icon: BarChart3,
      label: 'Competition',
      color: '#ec4899',
      isParent: true,
      children: [
        { id: 'competitor-analysis', icon: Activity, label: 'Competitor Analysis', color: '#ec4899' }
      ]
    },
    { id: 'reports', icon: FileText, label: 'Reports', color: '#06b6d4' },
    ...(isAdmin ? [{ id: 'account-chat', icon: Bot, label: 'AI Account Chat', color: '#8b5cf6', badge: 'Admin' }] : []),
    ...((isPartner || isAdmin) ? [{ id: 'partner', icon: Building2, label: 'Partner Dashboard', color: '#10b981', badge: 'Partner' }] : []),
    ...(affiliateStatus === 'approved' || affiliateStatus === 'paused' || affiliateStatus === 'pending'
        ? [{ id: 'affiliate-dashboard', icon: BadgePercent, label: 'Affiliate', color: '#8b5cf6', badge: affiliateStatus === 'pending' ? 'Pending' : 'Affiliate' }]
        : []),
    { id: 'settings', icon: Settings, label: 'Settings', color: '#64748b' },
    { id: 'help', icon: HelpCircle, label: 'Help & Support', color: '#6366f1' },
    ...(isAdmin ? [{ id: 'admin', icon: Shield, label: 'Admin Dashboard', color: '#ef4444', badge: 'Admin' }] : []),
    ...(isAdmin ? [{ id: 'admin-affiliates', icon: BadgePercent, label: 'Affiliates Admin', color: '#8b5cf6', badge: 'Admin' }] : [])
  ];

  const Competition = () => (
    <CompetitionDashboard selectedAccount={selectedAccount} />
  );

  const SettingsPanel = () => (
    <div style={{ padding: 40, color: theme.colors.text.primary }}>
      <h2 style={{ marginBottom: 20 }}>Settings</h2>
      <div style={{
        background: theme.colors.bg.secondary,
        padding: 30,
        borderRadius: 20,
        border: `1px solid ${theme.colors.border.primary}`
      }}>
        <p style={{ color: theme.colors.text.secondary }}>
          Settings panel coming soon...
        </p>
      </div>
    </div>
  );


  const handleCreateCampaignFromKeywords = (data) => {
    setSharedCampaignData(data);
    handleNavigate('campaign-builder');
  };

  const handleEditClonedCampaign = (campaign) => {
    setCampaignToEdit({
      ...campaign,
      source: 'cloner',
      isCloned: true
    });
    handleNavigate('campaign-builder');
  };

  const handleAICampaignGenerated = (data) => {
    setAiGeneratedCampaign(data);
    handleNavigate('campaign-builder');
  };

  const toggleMenuExpansion = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Track navigation to force dashboard remount
  const [dashboardKey, setDashboardKey] = useState(0);
  
  const renderContent = () => {
    switch(activeView) {
      case 'dashboard':
        // Key forces remount when navigating back to dashboard
        return <Dashboard key={`dashboard-${dashboardKey}`} selectedAccount={selectedAccount} onNavigate={handleNavigate} />;
      case 'getting-started':
        return <OnboardingChecklist onNavigate={handleNavigate} />;
      case 'account-audit':
        return <AccountAudit onNavigate={handleNavigate} />;
      case 'audit-log':
        return <AuditLog onNavigate={handleNavigate} />;
      case 'alert-rules':
        return <AlertRules />;
      case 'business-profile':
        return isAdmin ? <BusinessProfile selectedAccount={selectedAccount} /> : <Dashboard key={`dashboard-${dashboardKey}`} selectedAccount={selectedAccount} />;
      case 'bid-optimizer':
        return <OptimizationEngine />;
      case 'budget-allocator':
        return <BudgetAllocator selectedAccount={selectedAccount} />;
      case 'search-term-analyzer':
        return <SearchTermAnalyzer selectedAccount={selectedAccount} />;
      case 'offer-analyzer':
        return <OfferAnalyzer onAnalysisComplete={(data) => {
          if (data.action === 'use-in-campaign') {
            console.log('Using offer data in Campaign Builder:', data);
            setActiveView('campaign-builder');
          }
        }} />;
      case 'quality-score':
        return <QualityScoreOptimizer selectedAccount={selectedAccount} />;
      case 'pmax-optimizer':
        return <PmaxOptimizer selectedAccount={selectedAccount} />;
      case 'ai-generator':
        return isAdmin
          ? <AICampaignGenerator onCampaignGenerated={handleAICampaignGenerated} />
          : <Dashboard key={`dashboard-${dashboardKey}`} selectedAccount={selectedAccount} />;
      case 'campaign-builder':
        return <CampaignBuilder 
          initialKeywords={sharedCampaignData?.keywords} 
          initialLanguage={sharedCampaignData?.language}
          initialData={campaignToEdit || aiGeneratedCampaign || sharedCampaignData}
          onClose={() => {
            setSharedCampaignData(null);
            setAiGeneratedCampaign(null);
            setCampaignToEdit(null);
          }}
        />;
      case 'keyword-research':
        return <KeywordResearch onCreateCampaign={handleCreateCampaignFromKeywords} />;
      case 'ad-creator':
        return <AdCreator />;
      case 'campaign-cloner':
        return <CampaignCloner onEditInBuilder={handleEditClonedCampaign} />;
      case 'ab-testing':
        return <ABTesting />;
      case 'product-feeds':
        return <ProductFeedManager />;
      case 'server-side-tracking':
        return <ServerSideTracking />;
      case 'competitor-analysis':
        return <Competition />;
      case 'competition':
        return <Competition />;
      case 'reports':
        return <Reports selectedAccount={selectedAccount} />;
      case 'account-chat':
        return isAdmin ? <AccountChat selectedAccount={selectedAccount} /> : <Dashboard key={`dashboard-${dashboardKey}`} selectedAccount={selectedAccount} />;
      case 'settings':
        return <SettingsPage />;
      case 'subscription':
        return <SubscriptionPlans />;
      case 'help':
        return <HelpPage />;
      case 'partner':
        return (isPartner || isAdmin) ? <PartnerDashboard /> : <Dashboard key={`dashboard-${dashboardKey}`} selectedAccount={selectedAccount} />;
      case 'affiliate-dashboard':
        // Cross-sell only shows when the user has no Google Ads connection
        // — that's the pure-affiliate case where the rest of the app is empty.
        return <AffiliateDashboard showCrossSell={(allAccounts || []).length === 0} />;
      case 'admin':
        return isAdmin ? <AdminDashboard /> : <Dashboard key={`dashboard-${dashboardKey}`} selectedAccount={selectedAccount} />;
      case 'admin-affiliates':
        return isAdmin ? <AdminAffiliates /> : <Dashboard key={`dashboard-${dashboardKey}`} selectedAccount={selectedAccount} />;
      default:
        return <Dashboard key={`dashboard-${dashboardKey}`} selectedAccount={selectedAccount} />;
    }
  };

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      // background removed - inherited from ThemeWrapper
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      overflow: 'hidden'
    },
    sidebar: {
      width: isMobile ? 280 : (sidebarOpen ? 280 : 80),
      background: theme.colors.bg.secondary,
      backdropFilter: 'blur(10px)',
      borderRight: `1px solid ${theme.colors.border.primary}`,
      display: isMobile ? (mobileSidebarOpen ? 'flex' : 'none') : 'flex',
      flexDirection: 'column',
      transition: 'width 0.3s ease',
      overflow: 'visible', // Changed from hidden to visible for dropdown
      position: isMobile ? 'fixed' : 'relative',
      zIndex: 1000, // Increased so dropdown shows above content
      ...(isMobile && {
        left: 0,
        top: 0,
        bottom: 0,
        display: 'flex',
        transform: mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s ease'
      })
    },
    sidebarHeader: {
      padding: '25px 20px',
      borderBottom: `1px solid ${theme.colors.border.primary}`
    },
    logo: {
      fontSize: 20,
      fontWeight: 700,
      margin: 0,
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent'
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '14px 20px',
      margin: '5px 10px',
      borderRadius: 12,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      color: theme.colors.text.secondary,
      position: 'relative'
    },
    menuItemActive: {
      background: theme.colors.bg.hover,
      color: theme.colors.text.primary,
      borderLeft: `3px solid ${theme.colors.brand.primary}`
    },
    badge: {
      position: 'absolute',
      right: 15,
      background: 'rgba(245, 158, 11, 0.2)',
      color: '#fbbf24',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 10,
      fontWeight: 600
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    topBar: {
      height: 70,
      background: theme.colors.bg.secondary,
      backdropFilter: 'blur(10px)',
      borderBottom: `1px solid ${theme.colors.border.primary}`,
      display: 'flex',
      alignItems: 'center',
      padding: '0 30px',
      justifyContent: 'space-between',
      position: 'relative',
      zIndex: 100 // Ensures UserMenu dropdown shows above content
    },
    contentArea: {
      flex: 1,
      overflowY: 'auto',
      position: 'relative',
      zIndex: 1 // Lower than sidebar so dropdown shows on top
      // background removed - inherited from ThemeWrapper
    }
  };

  return (
    <div style={styles.container}>
      {/* Mobile Sidebar Overlay */}
      {isMobile && mobileSidebarOpen && (
        <div 
          className="mobile-sidebar-overlay active"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div 
        style={{
          ...styles.sidebar,
          ...(isMobile ? {
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            width: 280,
            transform: mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.3s ease',
            zIndex: 1001
          } : {})
        }}
        className={`app-sidebar ${mobileSidebarOpen ? 'open' : ''}`}
      >
        {/* Header */}
        <div style={styles.sidebarHeader}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {(sidebarOpen || isMobile) ? (
              <div 
                onClick={() => {
                  clearAccount();
                  if (isMobile) setMobileSidebarOpen(false);
                }}
                style={{ cursor: 'pointer' }}
              >
                <h1 style={styles.logo}>Bidoryx</h1>
                <p style={{ color: theme.colors.text.tertiary, fontSize: 12, margin: '5px 0 0 0' }}>
                  AI-Powered Google Ads
                </p>
              </div>
            ) : (
              <div 
                onClick={() => clearAccount()}
                style={{ width: 40, textAlign: 'center', cursor: 'pointer' }}
              >
                <Target size={24} style={{ color: '#6366f1' }} />
              </div>
            )}
            {isMobile ? (
              <button
                onClick={() => setMobileSidebarOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: theme.colors.text.tertiary,
                  cursor: 'pointer',
                  padding: 8
                }}
              >
                <X size={20} />
              </button>
            ) : (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="desktop-menu-toggle"
                style={{
                  background: 'none',
                  border: 'none',
                  color: theme.colors.text.tertiary,
                  cursor: 'pointer',
                  padding: 8
                }}
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            )}
          </div>
        </div>

        {/* Account Switcher with Dropdown */}
        {sidebarOpen && selectedAccount && (
          <div style={{ position: 'relative' }}>
            <div 
              style={{
                padding: '15px 20px',
                borderBottom: `1px solid ${theme.colors.border.primary}`,
                cursor: 'pointer',
                transition: 'background 0.2s',
                background: showAccountDropdown ? theme.colors.bg.hover : 'transparent'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowAccountDropdown(!showAccountDropdown);
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.bg.hover}
              onMouseLeave={(e) => e.currentTarget.style.background = showAccountDropdown ? theme.colors.bg.hover : 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Building2 size={16} color="#3b82f6" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: theme.colors.text.primary, fontWeight: 600 }}>
                    {selectedAccount.name}
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.text.tertiary, marginTop: 2 }}>
                    ID: {selectedAccount.id}
                  </div>
                </div>
                <ChevronDown 
                  size={14} 
                  color={theme.colors.text.tertiary}
                  style={{ 
                    transform: showAccountDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }} 
                />
              </div>
            </div>
            
            {/* Dropdown Menu */}
            {showAccountDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: theme.colors.bg.secondary,
                border: `1px solid ${theme.colors.border.primary}`,
                borderTop: 'none',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: theme.mode === 'light' 
                  ? '0 8px 24px rgba(0, 0, 0, 0.12)' 
                  : '0 4px 6px rgba(0, 0, 0, 0.3)'
              }}>
                {/* View All Accounts Link */}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAccountDropdown(false);
                    clearAccount();
                  }}
                  style={{
                    padding: '12px 20px',
                    fontSize: 13,
                    color: '#3b82f6',
                    fontWeight: 600,
                    cursor: 'pointer',
                    borderBottom: `1px solid ${theme.colors.border.primary}`,
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  📊 View All Accounts
                </div>
                
                {/* Account List - grouped by MCC hierarchy */}
                {(() => {
                  const savedLinked = localStorage.getItem('linkedGoogleAdsAccounts');
                  const linkedAccountIds = savedLinked ? JSON.parse(savedLinked) : [];
                  const linkedAccounts = allAccounts.filter(acc => 
                    linkedAccountIds.includes(acc.id) && acc.id !== selectedAccount.id
                  );

                  if (allAccounts.length === 0) {
                    return (
                      <div style={{
                        padding: '12px 20px',
                        fontSize: 13,
                        color: theme.colors.text.tertiary,
                        textAlign: 'center'
                      }}>
                        Loading accounts...
                      </div>
                    );
                  }

                  if (linkedAccounts.length === 0) {
                    return (
                      <div style={{
                        padding: '12px 20px',
                        fontSize: 13,
                        color: theme.colors.text.tertiary,
                        textAlign: 'center'
                      }}>
                        No other linked accounts.
                        <br />
                        <span style={{ fontSize: 11 }}>Click "View All Accounts" to link more.</span>
                      </div>
                    );
                  }

                  const renderAccountItem = (account, indent = false) => (
                    <div
                      key={account.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log(`🔄 Switching to account: ${account.name} (${account.id})`);
                        localStorage.setItem('selectedGoogleAdsAccount', JSON.stringify(account));
                        selectAccount(account);
                        setShowAccountDropdown(false);
                        setTimeout(() => {
                          if (window.location.pathname === '/') {
                            window.location.reload();
                          } else {
                            window.location.href = '/';
                          }
                        }, 100);
                      }}
                      style={{
                        padding: indent ? '10px 20px 10px 36px' : '12px 20px',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        borderBottom: `1px solid ${theme.colors.border.secondary}`
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.bg.hover}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ fontSize: 13, color: theme.colors.text.primary, fontWeight: 500, marginBottom: 4 }}>
                        {account.name}
                      </div>
                      <div style={{ fontSize: 11, color: theme.colors.text.muted }}>
                        ID: {account.id}
                      </div>
                    </div>
                  );

                  // If we have MCC hierarchy, group accounts
                  if (mccHierarchy && mccHierarchy.mccAccounts.length > 0) {
                    const rendered = [];
                    const renderedIds = new Set();

                    for (const mcc of mccHierarchy.mccAccounts) {
                      const childIds = mccHierarchy.hierarchy[mcc.id]?.children || [];
                      const mccChildren = linkedAccounts.filter(acc => childIds.includes(acc.id));
                      
                      if (mccChildren.length === 0) continue;

                      // MCC header
                      rendered.push(
                        <div key={`mcc-${mcc.id}`} style={{
                          padding: '8px 20px 4px',
                          fontSize: 10,
                          fontWeight: 700,
                          color: theme.mode === 'light' ? '#2563eb' : 'rgba(59, 130, 246, 0.85)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          borderTop: rendered.length > 0 ? `1px solid ${theme.colors.border.primary}` : 'none'
                        }}>
                          {mcc.name || `MCC ${mcc.id}`}
                        </div>
                      );

                      for (const account of mccChildren) {
                        rendered.push(renderAccountItem(account, true));
                        renderedIds.add(account.id);
                      }
                    }

                    // Render orphan accounts (no MCC parent)
                    const orphans = linkedAccounts.filter(acc => !renderedIds.has(acc.id));
                    if (orphans.length > 0 && rendered.length > 0) {
                      rendered.push(
                        <div key="orphan-header" style={{
                          padding: '8px 20px 4px',
                          fontSize: 10,
                          fontWeight: 700,
                          color: theme.colors.text.muted,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          borderTop: `1px solid ${theme.colors.border.primary}`
                        }}>
                          Direct Accounts
                        </div>
                      );
                    }
                    for (const account of orphans) {
                      rendered.push(renderAccountItem(account, orphans.length > 0 && rendered.length > orphans.length));
                      renderedIds.add(account.id);
                    }

                    return rendered;
                  }

                  // Fallback: flat list (no hierarchy available)
                  return linkedAccounts.map(account => renderAccountItem(account, false));
                })()}
              </div>
            )}
          </div>
        )}

        {/* Menu Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 0' }}>
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            
            if (item.isParent) {
              // Parent menu with collapsible children
              const isExpanded = expandedMenus[item.id];
              const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;
              
              return (
                <React.Fragment key={item.id}>
                  <div
                    style={{
                      ...styles.menuItem,
                      cursor: 'pointer'
                    }}
                    onClick={() => toggleMenuExpansion(item.id)}
                    title={!sidebarOpen ? item.label : ''}
                  >
                    <Icon size={20} style={{ 
                      marginRight: sidebarOpen ? 12 : 0, 
                      color: item.color 
                    }} />
                    {sidebarOpen && (
                      <React.Fragment>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{item.label}</span>
                        <ChevronIcon size={16} style={{ 
                          marginLeft: 'auto',
                          color: 'rgba(255,255,255,0.4)',
                          transition: 'transform 0.2s'
                        }} />
                      </React.Fragment>
                    )}
                  </div>
                  
                  {/* Child items */}
                  {isExpanded && sidebarOpen && item.children && item.children.map(child => {
                    const ChildIcon = child.icon;
                    const isChildActive = activeView === child.id;
                    
                    return (
                      <div
                        key={child.id}
                        style={{
                          ...styles.menuItem,
                          paddingLeft: 52,
                          ...(isChildActive ? styles.menuItemActive : {}),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                        onClick={() => handleNavigate(child.id)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <ChildIcon size={18} style={{ 
                            marginRight: 12, 
                            color: child.color 
                          }} />
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{child.label}</span>
                        </div>
                        {child.badge && (
                          <span style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: 10,
                            marginLeft: 8,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {child.badge}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            } else {
              // Regular menu item
              return (
                <div
                  key={item.id}
                  style={{
                    ...styles.menuItem,
                    ...(isActive ? styles.menuItemActive : {})
                  }}
                  onClick={() => handleNavigate(item.id)}
                  title={!sidebarOpen ? item.label : ''}
                >
                  <Icon size={20} style={{ 
                    marginRight: sidebarOpen ? 12 : 0, 
                    color: item.color 
                  }} />
                  {sidebarOpen && (
                    <React.Fragment>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{item.label}</span>
                      {item.badge && (
                        <span style={styles.badge}>{item.badge}</span>
                      )}
                    </React.Fragment>
                  )}
                </div>
              );
            }
          })}
        </div>

        {/* Footer */}
        {sidebarOpen && (
          <div style={{
            padding: 20,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            fontSize: 12,
            color: 'rgba(255, 255, 255, 0.4)'
          }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
              <a 
                href="/privacy" 
                style={{ 
                  color: 'rgba(255, 255, 255, 0.5)', 
                  textDecoration: 'none',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#6366f1'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
              >
                Privacy
              </a>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>•</span>
              <a 
                href="/terms" 
                style={{ 
                  color: 'rgba(255, 255, 255, 0.5)', 
                  textDecoration: 'none',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#6366f1'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
              >
                Terms
              </a>
            </div>
            <div style={{ marginTop: 5 }}>© 2026 Bidoryx</div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div style={styles.mainContent}>
        {/* Top Bar */}
        <div style={{...styles.topBar, padding: isMobile ? '0 16px' : '0 30px'}} className="app-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Mobile Menu Button */}
            {isMobile && (
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="mobile-menu-toggle"
                style={{
                  background: theme.colors.bg.hover,
                  border: `1px solid ${theme.colors.border.primary}`,
                  borderRadius: 8,
                  padding: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Menu size={20} color={theme.colors.text.secondary} />
              </button>
            )}
            <h2 style={{ color: theme.colors.text.primary, fontSize: isMobile ? 18 : 24, fontWeight: 600, margin: 0 }}>
              {(() => {
                // Check top-level items
                const topLevel = menuItems.find(m => m.id === activeView);
                if (topLevel) return topLevel.label;
                
                // Check nested items
                for (const item of menuItems) {
                  if (item.children) {
                    const child = item.children.find(c => c.id === activeView);
                    if (child) return child.label;
                  }
                }
                
                return 'Dashboard';
              })()}
            </h2>
          </div>

          <div style={{ display: 'flex', gap: isMobile ? 8 : 15, alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <button 
                onClick={(e) => { e.stopPropagation(); setShowNotifications(!showNotifications); }}
                style={{
                  position: 'relative',
                  background: showNotifications 
                    ? 'rgba(59, 130, 246, 0.15)' 
                    : theme.colors.bg.hover,
                  border: `1px solid ${showNotifications 
                    ? 'rgba(59, 130, 246, 0.3)' 
                    : theme.colors.border.primary}`,
                  borderRadius: 8,
                  padding: 8,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <Bell 
                  size={20} 
                  color={showNotifications ? '#3b82f6' : theme.colors.text.secondary}
                />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    minWidth: 18,
                    height: 18,
                    padding: '0 5px',
                    borderRadius: 9,
                    background: '#ef4444',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px solid ${theme.colors.bg.primary}`,
                    boxSizing: 'content-box',
                    lineHeight: 1
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              
              {showNotifications && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    width: 380,
                    maxWidth: 'calc(100vw - 32px)',
                    background: theme.colors.bg.secondary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: 12,
                    boxShadow: theme.mode === 'light'
                      ? '0 8px 24px rgba(0, 0, 0, 0.12)'
                      : '0 8px 24px rgba(0, 0, 0, 0.4)',
                    zIndex: 1000,
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    padding: '12px 16px',
                    borderBottom: `1px solid ${theme.colors.border.secondary}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: 600, color: theme.colors.text.primary }}>
                        Notifications
                      </span>
                      {unreadCount > 0 && (
                        <span style={{
                          background: 'rgba(239, 68, 68, 0.15)',
                          color: '#ef4444',
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 10
                        }}>
                          {unreadCount} new
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {unreadCount > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); markAllNotificationsRead(); }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            background: 'transparent',
                            border: 'none',
                            color: theme.colors.text.secondary,
                            fontSize: 12,
                            cursor: 'pointer',
                            padding: '4px 6px',
                            borderRadius: 6
                          }}
                          title="Mark all as read"
                          onMouseEnter={(e) => { e.currentTarget.style.background = theme.colors.bg.hover; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <CheckCheck size={14} />
                          Mark all read
                        </button>
                      )}
                      {notifications.length > 0 ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); dismissAllNotifications(); }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            background: 'transparent',
                            border: 'none',
                            color: theme.colors.text.secondary,
                            fontSize: 12,
                            cursor: 'pointer',
                            padding: '4px 6px',
                            borderRadius: 6
                          }}
                          title="Clear all notifications"
                          onMouseEnter={(e) => { e.currentTarget.style.background = theme.colors.bg.hover; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <X size={14} />
                          Clear all
                        </button>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: theme.colors.text.tertiary }}>
                          All caught up
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                    {notificationsLoading && notifications.length === 0 ? (
                      <div style={{
                        padding: '2rem 1.5rem',
                        textAlign: 'center',
                        color: theme.colors.text.tertiary,
                        fontSize: 13
                      }}>
                        Loading notifications…
                      </div>
                    ) : notifications.length === 0 ? (
                      <div style={{
                        padding: '2rem 1.5rem',
                        textAlign: 'center',
                        color: theme.colors.text.tertiary
                      }}>
                        <Bell size={32} color={theme.colors.text.muted} style={{ margin: '0 auto 12px' }} />
                        <div style={{ fontSize: '0.9rem', marginBottom: 4, color: theme.colors.text.secondary }}>
                          No new notifications
                        </div>
                        <div style={{ fontSize: '0.8rem', color: theme.colors.text.muted }}>
                          Alerts and updates will appear here
                        </div>
                      </div>
                    ) : (
                      notifications.map(n => {
                        const sevColor = n.severity === 'critical' || n.severity === 'high'
                          ? '#ef4444'
                          : n.severity === 'warning'
                            ? '#f59e0b'
                            : n.severity === 'success'
                              ? '#10b981'
                              : '#3b82f6';
                        const SevIcon = n.severity === 'critical' || n.severity === 'high'
                          ? AlertCircle
                          : n.severity === 'warning'
                            ? AlertTriangle
                            : n.severity === 'success'
                              ? CheckCircle2
                              : n.type === 'ad_spend_warning' || n.type === 'ad_spend_over'
                                ? DollarSign
                                : Info;
                        const isHovered = hoveredNotificationKey === n.key;
                        return (
                          <div
                            key={n.key}
                            onClick={() => handleNotificationClick(n)}
                            style={{
                              padding: '12px 16px',
                              borderBottom: `1px solid ${theme.colors.border.secondary}`,
                              cursor: 'pointer',
                              background: n.isRead
                                ? 'transparent'
                                : (theme.mode === 'light'
                                  ? 'rgba(59, 130, 246, 0.06)'
                                  : 'rgba(59, 130, 246, 0.08)'),
                              transition: 'background 0.15s',
                              display: 'flex',
                              gap: 12,
                              position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = theme.colors.bg.hover;
                              setHoveredNotificationKey(n.key);
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = n.isRead
                                ? 'transparent'
                                : (theme.mode === 'light'
                                  ? 'rgba(59, 130, 246, 0.06)'
                                  : 'rgba(59, 130, 246, 0.08)');
                              setHoveredNotificationKey(prev => (prev === n.key ? null : prev));
                            }}
                          >
                            <div style={{
                              flexShrink: 0,
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: `${sevColor}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <SevIcon size={16} color={sevColor} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: 8
                              }}>
                                <span style={{
                                  fontSize: 13,
                                  fontWeight: n.isRead ? 500 : 600,
                                  color: theme.colors.text.primary,
                                  lineHeight: 1.3,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical'
                                }}>
                                  {n.title}
                                </span>
                                {!n.isRead && (
                                  <span style={{
                                    flexShrink: 0,
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: sevColor,
                                    marginTop: 5
                                  }} />
                                )}
                              </div>
                              {n.message && (
                                <div style={{
                                  fontSize: 12,
                                  color: theme.colors.text.secondary,
                                  marginTop: 4,
                                  lineHeight: 1.4
                                }}>
                                  {n.message}
                                </div>
                              )}
                              <div style={{
                                fontSize: 11,
                                color: theme.colors.text.muted,
                                marginTop: 6,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}>
                                <span>{formatRelativeTime(n.timestamp)}</span>
                                {n.actionLabel && (
                                  <span style={{ color: sevColor, fontWeight: 600 }}>
                                    · {n.actionLabel}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); dismissNotification(n); }}
                              aria-label="Dismiss notification"
                              title="Dismiss"
                              style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                width: 22,
                                height: 22,
                                borderRadius: 6,
                                border: 'none',
                                background: isHovered ? theme.colors.bg.tertiary : 'transparent',
                                color: theme.colors.text.muted,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: isHovered ? 1 : 0,
                                transition: 'opacity 0.15s, background 0.15s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = theme.colors.bg.hover;
                                e.currentTarget.style.color = theme.colors.text.primary;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = theme.colors.bg.tertiary;
                                e.currentTarget.style.color = theme.colors.text.muted;
                              }}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <UserMenu onShowSubscription={() => setShowSubscriptionModal(true)} />
          </div>
        </div>

        {/* Content Area */}
        <div style={styles.contentArea}>
          {renderContent()}
        </div>
        
        <style>{`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.95; }
          }
        `}</style>
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <SubscriptionPlans onClose={() => setShowSubscriptionModal(false)} />
      )}

      {/* Trial Expired Modal */}
      <TrialExpiredModal 
        isOpen={showTrialExpiredModal}
        onClose={() => setShowTrialExpiredModal(false)}
        onUpgrade={() => {
          setShowTrialExpiredModal(false);
          setShowSubscriptionModal(true);
        }}
      />
    </div>
  );
}

// Wrapper to check if account is selected
function AppWithAccountCheck() {
  const { selectedAccount, isAccountSelected, selectAccount, clearAccount } = useAccount();
  const { isBlocked, isLoading: adSpendLoading } = useAdSpendBlocker();

  if (!isAccountSelected) {
    // Check if user had a previously selected account (came from "View All Accounts")
    const lastAccount = localStorage.getItem('lastSelectedGoogleAdsAccount');
    const handleBack = lastAccount ? () => {
      try {
        const account = JSON.parse(lastAccount);
        localStorage.removeItem('showAccountOverview');
        selectAccount(account);
      } catch (e) {
        console.error('Failed to restore account:', e);
      }
    } : null;
    
    return <AccountSelector onAccountSelect={selectAccount} onBack={handleBack} />;
  }

  if (!adSpendLoading && isBlocked) {
    return <AdSpendBlocker />;
  }

  return <AppContent selectedAccount={selectedAccount} clearAccount={clearAccount} />;
}

// Public wrapper - minimal public routes, rest requires auth
function PublicRoutes() {
  return (
    <ErrorBoundary>
      <Router>
        <ThemeWrapper>
          <ToastProvider>
            <Routes>
              {/* Public routes - NO authentication required */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/invite/:token" element={<AcceptInvite />} />
              {/* Affiliate signup is public — Clerk sign-in is rendered inside the page */}
              <Route path="/affiliate/signup" element={<AffiliateSignup />} />
              
              {/* Root and all other routes require authentication */}
              {/* Landing page is now on bidoryx.com, app.bidoryx.com goes straight to login/dashboard */}
              <Route path="/*" element={<AuthenticatedApp />} />
            </Routes>
          </ToastProvider>
        </ThemeWrapper>
      </Router>
    </ErrorBoundary>
  );
}

// Authenticated app wrapper
function AuthenticatedApp() {
  return (
    <AuthGuard>
      <AccountProvider>
        <Routes>
          {/* Routes accessible without account selection or tier limit check */}
          <Route path="/billing/success" element={<CheckoutSuccess />} />
          <Route path="/billing/cancel" element={<CheckoutCancel />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/subscription" element={<SubscriptionPlans onClose={() => window.history.back()} />} />
          
          {/* Main app routes - require account selection AND tier compliance */}
          <Route path="/*" element={<AppWithAccountCheck />} />
        </Routes>
      </AccountProvider>
    </AuthGuard>
  );
}

function App() {
  return <PublicRoutes />;
}

export default App;